import { useId } from 'react';
import { Button } from './button';
import { Checkbox } from './checkbox';
import { Input } from './input';

export interface AgreementReference {
  instrumentId: string;
  scopeId: string;
  definitionVersion: string;
}
export interface AgreementSelectionOption extends AgreementReference {
  family: 'cct' | 'ctt';
  title: string;
  population: string;
  conditions: string[];
  cantons: string[];
  validFrom: string;
  validUntil?: string;
  incompletePayroll: boolean;
}
export type AgreementSelectionValue = Partial<
  Record<
    'cct' | 'ctt',
    {
      status: 'complete' | 'incomplete';
      applicable: AgreementReference[];
    }
  >
>;
export interface AgreementSelectionLabels {
  search: string;
  loading: string;
  empty: string;
  noResults: string;
  hidden: string;
  conditions: string;
  population: string;
  cantons: string;
  validity: string;
  coverage: string;
  complete: string;
  incomplete: string;
  confirm: string;
  none: string;
  unknown: string;
  reset: string;
}
const sameReference = (a: AgreementReference, b: AgreementReference) =>
  a.instrumentId === b.instrumentId &&
  a.scopeId === b.scopeId &&
  a.definitionVersion === b.definitionVersion;

/** Controlled selector. Opaque saved references survive filtering and search. */
export function AgreementSelection({
  options,
  value,
  onChange,
  search,
  onSearchChange,
  labels,
  loading,
  disabled,
}: {
  options: AgreementSelectionOption[];
  value: AgreementSelectionValue;
  onChange: (value: AgreementSelectionValue) => void;
  search: string;
  onSearchChange: (search: string) => void;
  labels: AgreementSelectionLabels;
  loading?: boolean;
  disabled?: boolean;
}) {
  const id = useId();
  return (
    <fieldset disabled={disabled || loading} className="min-w-0 space-y-4">
      <label htmlFor={`${id}-search`}>{labels.search}</label>
      <Input
        id={`${id}-search`}
        type="search"
        value={search}
        onChange={(e) => onSearchChange(e.target.value)}
      />
      {loading ? (
        <p role="status">{labels.loading}</p>
      ) : options.length === 0 ? (
        <p role="status">{labels.empty}</p>
      ) : null}
      {(['cct', 'ctt'] as const).map((family) => {
        const assessment = value[family] ?? {
          status: 'incomplete',
          applicable: [],
        };
        const familyOptions = options.filter((o) => o.family === family);
        const hidden =
          !loading &&
          assessment.applicable.some(
            (s) => !familyOptions.some((o) => sameReference(o, s)),
          );
        const visible = familyOptions.filter((o) =>
          `${o.title} ${o.population}`
            .toLocaleLowerCase()
            .includes(search.toLocaleLowerCase().trim()),
        );
        const update = (next: typeof assessment) =>
          onChange({ ...value, [family]: next });
        return (
          <fieldset
            key={family}
            className="min-w-0 space-y-3 rounded-lg border p-3"
          >
            <legend className="px-1 font-semibold">
              {family.toUpperCase()} — {labels[assessment.status]}
            </legend>
            {hidden && (
              <p role="status" className="text-sm text-muted-foreground">
                {labels.hidden}
              </p>
            )}
            {!loading && familyOptions.length > 0 && visible.length === 0 && (
              <p>{labels.noResults}</p>
            )}
            {visible.map((option, index) => {
              const checked = assessment.applicable.some((s) =>
                sameReference(s, option),
              );
              // A hidden older version must be removed explicitly before replacement.
              const hiddenVersion = assessment.applicable.some(
                (s) =>
                  s.scopeId === option.scopeId && !sameReference(s, option),
              );
              return (
                <div
                  key={`${option.scopeId}:${option.definitionVersion}`}
                  className="space-y-2 break-words"
                >
                  <div className="flex items-start gap-2">
                    <Checkbox
                      id={`${id}-${family}-${index}`}
                      checked={checked}
                      disabled={disabled || loading || hiddenVersion}
                      onCheckedChange={(selected) =>
                        update({
                          status: 'incomplete',
                          applicable:
                            selected === true
                              ? [
                                  ...assessment.applicable,
                                  {
                                    instrumentId: option.instrumentId,
                                    scopeId: option.scopeId,
                                    definitionVersion: option.definitionVersion,
                                  },
                                ]
                              : assessment.applicable.filter(
                                  (s) => !sameReference(s, option),
                                ),
                        })
                      }
                    />
                    <label htmlFor={`${id}-${family}-${index}`}>
                      {option.title}
                    </label>
                  </div>
                  {option.incompletePayroll && (
                    <p className="text-sm text-muted-foreground">
                      {labels.coverage}
                    </p>
                  )}
                  <details className="text-sm">
                    <summary className="cursor-pointer">
                      {labels.conditions}
                    </summary>
                    <dl className="mt-2 space-y-1">
                      <dt className="font-medium">{labels.population}</dt>
                      <dd>{option.population}</dd>
                      {option.conditions.length > 0 && (
                        <>
                          <dt className="font-medium">{labels.conditions}</dt>
                          <dd>{option.conditions.join(' · ')}</dd>
                        </>
                      )}
                      <dt className="font-medium">{labels.cantons}</dt>
                      <dd>{option.cantons.join(', ')}</dd>
                      <dt className="font-medium">{labels.validity}</dt>
                      <dd>
                        {option.validFrom} – {option.validUntil ?? '∞'}
                      </dd>
                    </dl>
                  </details>
                </div>
              );
            })}
            <div className="flex flex-wrap gap-2">
              <Button
                className="h-auto min-h-10 max-w-full whitespace-normal"
                type="button"
                variant="outline"
                disabled={hidden || assessment.applicable.length === 0}
                onClick={() => update({ ...assessment, status: 'complete' })}
              >
                {labels.confirm}
              </Button>
              <Button
                className="h-auto min-h-10 max-w-full whitespace-normal"
                type="button"
                variant="outline"
                aria-pressed={
                  assessment.status === 'complete' &&
                  assessment.applicable.length === 0
                }
                onClick={() => update({ status: 'complete', applicable: [] })}
              >
                {labels.none}
              </Button>
              <Button
                className="h-auto min-h-10 max-w-full whitespace-normal"
                type="button"
                variant="outline"
                aria-pressed={assessment.status === 'incomplete'}
                onClick={() => update({ ...assessment, status: 'incomplete' })}
              >
                {labels.unknown}
              </Button>
              <Button
                className="h-auto min-h-10 max-w-full whitespace-normal"
                type="button"
                variant="outline"
                onClick={() => update({ status: 'incomplete', applicable: [] })}
              >
                {labels.reset}
              </Button>
            </div>
          </fieldset>
        );
      })}
    </fieldset>
  );
}
